// web/src/pages/payments/PaymentsListPage.tsx
//
// I-7 — Payments list page.
//
// Filters: status (multi-select pills), paymentTypeCode, accountId, date range.
// Saved-view chips: "Pending", "Posted today", "Reversed".
// "Record Payment" CTA opens RecordPaymentModal (no invoice context).
// URL ?openModal=true auto-opens the modal on mount.
import { useMemo, useState, useEffect } from "react";
import { useSearchParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import {
  listPayments,
  type PaymentRecordSummaryDto,
} from "@/lib/invoices-payments";
import { PaymentMethodBadge } from "@/components/payment-method-badge";
import { ListPage } from "@/archetypes/ListPage";
import { DataGrid, type DataGridColumn } from "@/components/data-grid";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { IntentButton } from "@/components/intent-button";
import { RecordPaymentModal } from "./RecordPaymentModal";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type PaymentStatusCode = "Pending" | "Posted" | "Reversed" | "Failed";

type SavedViewId = "all" | "pending" | "postedToday" | "reversed";

interface FilterState {
  statuses: PaymentStatusCode[];
  paymentTypeCode: string;
  accountId: string;
  from: string;
  to: string;
}

const EMPTY_FILTERS: FilterState = {
  statuses: [],
  paymentTypeCode: "",
  accountId: "",
  from: "",
  to: "",
};

const ALL_STATUSES: PaymentStatusCode[] = ["Pending", "Posted", "Reversed", "Failed"];

const SAVED_VIEWS: { id: SavedViewId; label: string }[] = [
  { id: "all",         label: "All" },
  { id: "pending",     label: "Pending" },
  { id: "postedToday", label: "Posted today" },
  { id: "reversed",    label: "Reversed" },
];

function applySavedView(view: SavedViewId): FilterState {
  switch (view) {
    case "pending":
      return { ...EMPTY_FILTERS, statuses: ["Pending"] };
    case "postedToday": {
      const start = new Date();
      start.setUTCHours(0, 0, 0, 0);
      return { ...EMPTY_FILTERS, statuses: ["Posted"], from: start.toISOString() };
    }
    case "reversed":
      return { ...EMPTY_FILTERS, statuses: ["Reversed"] };
    case "all":
    default:
      return EMPTY_FILTERS;
  }
}

// ---------------------------------------------------------------------------
// Currency formatter
// ---------------------------------------------------------------------------

const fmt = new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" });

// ---------------------------------------------------------------------------
// Build params from filter state
// ---------------------------------------------------------------------------

function buildParams(state: FilterState, page: number) {
  return {
    status: state.statuses.length > 0 ? state.statuses : undefined,
    paymentTypeCode: state.paymentTypeCode || undefined,
    accountId: state.accountId ? Number(state.accountId) : undefined,
    from: state.from || undefined,
    to: state.to || undefined,
    page,
    pageSize: 50,
  };
}

// ---------------------------------------------------------------------------
// PaymentsListPage component interface
// ---------------------------------------------------------------------------

export interface PaymentsListPageProps {
  /** Reserved for future use (e.g. pre-filter by account). */
  initialAccountId?: number;
}

// ---------------------------------------------------------------------------
// PaymentsListPage
// ---------------------------------------------------------------------------

export function PaymentsListPage({ initialAccountId }: PaymentsListPageProps = {}) {
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState<FilterState>({
    ...EMPTY_FILTERS,
    accountId: initialAccountId != null ? String(initialAccountId) : "",
  });
  const [activeView, setActiveView] = useState<SavedViewId>("all");
  const [page, setPage] = useState(0);
  const [modalOpen, setModalOpen] = useState(() => searchParams.get("openModal") === "true");

  // Auto-open modal if ?openModal=true is present on mount.
  useEffect(() => {
    if (searchParams.get("openModal") === "true") {
      setModalOpen(true);
    }
  }, [searchParams]);

  const params = useMemo(() => buildParams(filters, page), [filters, page]);

  const { data, isLoading } = useQuery({
    queryKey: ["payments", params],
    queryFn: () => listPayments(params),
  });

  // Client-side multi-status filter (mirrors InvoicesListPage pattern).
  const visibleRows = useMemo(() => {
    const items = data?.items ?? [];
    if (filters.statuses.length > 1) {
      return items.filter((r) =>
        filters.statuses.includes(r.status as PaymentStatusCode),
      );
    }
    return items;
  }, [data, filters.statuses]);

  function applyView(id: SavedViewId) {
    setActiveView(id);
    setFilters(applySavedView(id));
    setPage(0);
  }

  function toggleStatus(s: PaymentStatusCode) {
    setActiveView("all");
    setPage(0);
    setFilters((f) => ({
      ...f,
      statuses: f.statuses.includes(s)
        ? f.statuses.filter((x) => x !== s)
        : [...f.statuses, s],
    }));
  }

  // ---------------------------------------------------------------------------
  // Columns
  // ---------------------------------------------------------------------------

  const cols: DataGridColumn<PaymentRecordSummaryDto>[] = [
    {
      id: "id",
      header: "Payment ID",
      accessor: (r) => String(r.paymentRecordId),
    },
    {
      id: "date",
      header: "Transaction date",
      accessor: (r) => new Date(r.transactionDate).toLocaleDateString("en-AU"),
    },
    {
      id: "account",
      header: "Account",
      accessor: (r) => (r.accountId != null ? `#${r.accountId}` : "—"),
    },
    {
      id: "invoice",
      header: "Invoice",
      accessor: (r) => (r.invoiceId != null ? `#${r.invoiceId}` : "—"),
    },
    {
      id: "type",
      header: "Type",
      accessor: (r) => <PaymentMethodBadge code={r.paymentTypeCode} />,
    },
    {
      id: "amount",
      header: "Amount",
      accessor: (r) => fmt.format(r.tranAmount),
      align: "right",
    },
    {
      id: "status",
      header: "Status",
      accessor: (r) => r.status,
    },
    {
      id: "allocations",
      header: "Allocations",
      accessor: (r) => String(r.allocationCount),
      align: "right",
    },
  ];

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <>
      <ListPage
        title="Payments"
        primaryAction={
          <IntentButton onClick={() => setModalOpen(true)}>
            + Record Payment
          </IntentButton>
        }
        filterBar={
          <div className="space-y-3">
            {/* Saved-view chips */}
            <div className="flex flex-wrap gap-2">
              {SAVED_VIEWS.map((v) => (
                <Button
                  key={v.id}
                  variant={activeView === v.id ? "default" : "outline"}
                  size="sm"
                  onClick={() => applyView(v.id)}
                >
                  {v.label}
                </Button>
              ))}
            </div>

            {/* Status pills (multi-select) */}
            <div className="flex flex-wrap gap-1">
              <span className="self-center text-xs text-muted-foreground mr-1">Status:</span>
              {ALL_STATUSES.map((s) => {
                const active = filters.statuses.includes(s);
                return (
                  <Button
                    key={s}
                    type="button"
                    variant={active ? "default" : "outline"}
                    size="sm"
                    onClick={() => toggleStatus(s)}
                  >
                    {s}
                  </Button>
                );
              })}
            </div>

            {/* Filter inputs */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              <div>
                <Label htmlFor="pmt-type">Payment type</Label>
                <Input
                  id="pmt-type"
                  value={filters.paymentTypeCode}
                  onChange={(e) => {
                    setActiveView("all");
                    setFilters({ ...filters, paymentTypeCode: e.target.value });
                    setPage(0);
                  }}
                  placeholder="e.g. EFT"
                />
              </div>
              <div>
                <Label htmlFor="pmt-account">Account ID</Label>
                <Input
                  id="pmt-account"
                  type="number"
                  value={filters.accountId}
                  onChange={(e) => {
                    setActiveView("all");
                    setFilters({ ...filters, accountId: e.target.value });
                    setPage(0);
                  }}
                />
              </div>
              <div>
                <Label htmlFor="pmt-from">Date from</Label>
                <Input
                  id="pmt-from"
                  type="date"
                  value={filters.from ? filters.from.slice(0, 10) : ""}
                  onChange={(e) => {
                    setActiveView("all");
                    setFilters({ ...filters, from: e.target.value });
                    setPage(0);
                  }}
                />
              </div>
              <div>
                <Label htmlFor="pmt-to">Date to</Label>
                <Input
                  id="pmt-to"
                  type="date"
                  value={filters.to ? filters.to.slice(0, 10) : ""}
                  onChange={(e) => {
                    setActiveView("all");
                    setFilters({ ...filters, to: e.target.value });
                    setPage(0);
                  }}
                />
              </div>
            </div>
          </div>
        }
      >
        <DataGrid
          rows={visibleRows}
          columns={cols}
          rowKey={(r) => r.paymentRecordId}
          totalRows={data?.totalCount ?? 0}
          pageIndex={page}
          pageSize={50}
          loading={isLoading}
          emptyHeadline="No payments match these filters."
          onPageChange={setPage}
        />
      </ListPage>

      <RecordPaymentModal
        open={modalOpen}
        onOpenChange={setModalOpen}
      />
    </>
  );
}
